'use client';

import React from 'react';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Service {
  id: string;
  title: string;
  badge?: string;
  subtitle: string;
  description: string;
  features: string[];
  featured?: boolean;
  smsMessage: string;
}

const services: Service[] = [
  {
    id: 'mini-detail',
    title: 'Mini Detail',
    subtitle: '(Exterior + Light Interior)',
    description: 'Perfect for keeping your vehicle looking sharp between full details. A quick, thorough refresh that makes a real difference.',
    features: [
      'Ceramic foam wash',
      '2 bucket hand wash',
      'Spot free rinse',
      'Air pressure & microfiber towel dry',
      'Tire & trim dressing',
      'Rim cleaning',
      'Vacuum floors and mats',
      'Interior and exterior window streak free cleaning',
    ],
    smsMessage: "Hi DA1MAMBA! I'd like a quote for a Mini Detail. My car details: [Year, Make, Model, Color]. Please let me know pricing and availability. Thanks!",
  },
  {
    id: 'full-exterior',
    title: 'Full Exterior Detail',
    subtitle: 'Luxury exterior detailing in Newport Beach & Irvine',
    description: 'A comprehensive exterior treatment that restores depth, gloss, and protection — using premium products and paint-safe methods your car deserves.',
    features: [
      'Touchless pre-rinse & premium foam bath',
      'Wheel, barrel, caliper & tire deep clean',
      'Hand wash with two-bucket method',
      'Clay bar & chemical decontamination',
      'Gentle drying — air + plush microfiber',
      'Trim & plastics cleaned and dressed',
      'Premium sealant or spray ceramic topper',
    ],
    smsMessage: "Hi DA1MAMBA! I'd like a quote for a Full Exterior Detail. My car details: [Year, Make, Model, Color]. Please let me know pricing and availability. Thanks!",
  },
  {
    id: 'full-interior',
    title: 'Full Interior Detail',
    subtitle: 'Luxury interior detailing in Newport Beach & Irvine',
    description: 'A deep interior reset that eliminates odors, lifts stains, and restores that fresh-from-the-showroom feeling you thought was gone for good.',
    features: [
      'Precision vacuum (seats, carpets, trunk)',
      'Shampoo & steam treatment',
      'Targeted stain extraction',
      'Leather clean & condition',
      'Panels, vents, buttons detailed',
      'Streak-free glass & sanitization',
    ],
    smsMessage: "Hi DA1MAMBA! I'd like a quote for a Full Interior Detail. My car details: [Year, Make, Model, Color]. Please let me know pricing and availability. Thanks!",
  },
  {
    id: 'boat-detailing',
    title: 'Boat Detailing',
    subtitle: 'Professional marine vessel detailing services',
    description: 'Your vessel works hard on the water. Our marine detailing keeps it looking pristine on and off the dock — hull to cabin.',
    features: [
      'Hull cleaning',
      'Deck washing and treatment',
      'Interior cabin detailing',
      'Upholstery cleaning and protection',
      'Metal and chrome polishing',
    ],
    smsMessage: "Hi DA1MAMBA! I'd like a quote for Boat Detailing. My vessel details: [Year, Make, Model, Length]. Please let me know pricing and availability. Thanks!",
  },
  {
    id: 'paint-correction',
    title: 'Paint Correction',
    subtitle: 'Dealership-grade paint correction in Newport Beach & Irvine',
    description: 'Swirls, haze, and light scratches don\'t stand a chance. Every stage is performed with conservative, paint-safe methodology — trusted on Ferrari, Lamborghini, McLaren, Porsche, Bentley, Rolls-Royce, Mercedes-AMG, BMW M, Audi RS, and Tesla.',
    features: [
      'Remove swirls, haze & light scratches safely',
      'Level 1: Enhancement polish (50-70% defect removal)',
      'Level 2: Two-step correction (70-90% removal)',
      'Level 3: Multi-step for severe defects',
      'Brand-new microfiber towels every vehicle',
      'Luxury & exotic safe — Ferrari to Tesla',
    ],
    smsMessage: "Hi DA1MAMBA! I'd like a quote for Paint Correction. My car details: [Year, Make, Model, Color, current paint condition]. Please let me know pricing and availability. Thanks!",
  },
  {
    id: 'ceramic-coating',
    title: 'Ceramic Coating',
    subtitle: 'Premium ceramic coating in Newport Beach, Irvine & Orange County',
    description: 'The smartest investment you can make in your paint. A long-term hydrophobic barrier that enhances gloss, repels dirt, blocks UV damage, and keeps your vehicle cleaner — longer.',
    features: [
      'Deep, glass-like gloss with hydrophobic beading',
      'UV and oxidation protection',
      'Swirl, light scratch, bug & bird droppings resistance',
      'Easier washing — dirt & grime cannot bond',
      'Long-term paint protection for resale value',
      'Entry level 6-month & premium 2+ year options',
    ],
    smsMessage: "Hi DA1MAMBA! I'd like a quote for Ceramic Coating. My car details: [Year, Make, Model, Color, mileage]. Please let me know pricing and availability. Thanks!",
  },
  {
    id: 'fleet-detailing',
    title: 'Fleet Detailing',
    subtitle: 'Commercial & fleet detailing services for businesses',
    description: 'Your fleet represents your brand. We keep every vehicle looking professional with flexible scheduling designed around your business — not the other way around.',
    features: [
      'Multi-vehicle packages available',
      'Scheduled maintenance detailing',
      'Dealership & rental fleet services',
      'Corporate & luxury fleet care',
      'Flexible scheduling for business needs',
    ],
    smsMessage: "Hi DA1MAMBA! I'd like a quote for Fleet Detailing. Fleet details: [Number of vehicles, vehicle types/makes]. Please let me know pricing and availability. Thanks!",
  },
];

const packages = [
  {
    name: 'Platinum Deluxe',
    features: [
      'Full interior & exterior detail',
      'Clay bar decontamination',
      '1-step polish',
      'Ceramic sealant',
    ],
  },
  {
    name: 'Luxury White Glove',
    features: [
      'Full interior shampoo + leather treatment',
      'Paint gloss enhancement polish',
      'Ceramic coating application',
      'Engine bay cleaning',
    ],
  },
  {
    name: 'Ultimate Experience',
    features: [
      'Multi-stage paint correction',
      'Premium ceramic coating (2+ year protection)',
      'Complete interior restoration',
      'Underhood detailing',
    ],
  },
];

const addOns = [
  'Clay Bar Decontamination',
  'Headlight Restoration',
  'Engine Bay Cleaning',
  'Pet Hair Removal',
  'Exterior Bug/Sap Removal',
  'Odor Treatment',
  'Tire Patching',
  'Window PPF Removal',
];

const CheckIcon = () => (
  <svg className="w-4 h-4 text-[#B8860B] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 text-center bg-[#111111] border-b border-[#2a2a2a]">
        <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">Services</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">What We Do Best</h1>
        <p className="text-[#9CA3AF] max-w-xl mx-auto mb-10">
          From a quick refresh to a full paint correction — every service is performed with the same obsessive attention to detail, at your home or office.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="sms:9492935721"
            className="w-full sm:w-auto bg-[#4B0082] hover:bg-[#6B21A8] text-white font-semibold px-8 py-3 rounded text-sm tracking-wide uppercase transition-colors duration-200"
          >
            Text for a Quote
          </a>
          <a
            href="tel:+19492935721"
            className="w-full sm:w-auto border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-black font-semibold px-8 py-3 rounded text-sm tracking-wide uppercase transition-colors duration-200"
          >
            Call Now
          </a>
        </div>
      </section>

      {/* DA1MAMBA DETAIL — Featured Hero Card */}
      <section className="py-10 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden border-2 border-[#4B0082] bg-[#1a1a1a]">
            <div className="absolute top-4 left-4 z-10">
              <span className="bg-[#B8860B] text-black text-xs font-bold px-3 py-1 tracking-widest uppercase">
                MOST POPULAR
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[400px]">
                <Image
                  src="https://da1mambadetailing.com/assets/416006343_685055570326614_4483350650480443101_n_1751763806956.jpg"
                  alt="DA1MAMBA Full Detail — Premium interior and exterior reset service"
                  fill
                  className="object-cover"
                  unoptimized
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#1a1a1a]/80 hidden lg:block" />
              </div>
              <div className="p-6 sm:p-8 lg:p-12 flex flex-col justify-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">DA1MAMBA DETAIL</h2>
                <p className="text-[#B8860B] text-sm font-medium tracking-wide mb-4">The Complete Reset — Newport Beach &amp; Irvine</p>
                <p className="text-[#9CA3AF] text-sm leading-relaxed mb-6">
                  Our signature service: a full interior + exterior transformation using shampoo &amp; steam, clay bar decontamination, gloss protection, and brand-new microfiber towels on every single vehicle. No shortcuts. No cross-contamination. Just results.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6 sm:mb-8">
                  {[
                    'Touchless pre-rinse & premium foam bath',
                    'Wheel, barrel, caliper & tire deep clean',
                    'Clay bar & chemical decontamination',
                    'Shampoo & steam treatment',
                    'Leather clean & condition',
                    'Panels, vents, buttons detailed',
                    'Streak-free glass & sanitization',
                    'Premium sealant or spray ceramic topper',
                  ].map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm text-[#D1D5DB]">
                      <CheckIcon />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
                <a
                  href={`sms:9492935721?body=${encodeURIComponent("Hi DA1MAMBA! I'd like a quote for the DA1MAMBA Full Detail. My car details: [Year, Make, Model, Color]. Please let me know pricing and availability. Thanks!")}`}
                  className="inline-block text-center bg-[#4B0082] hover:bg-[#6B21A8] text-white text-sm font-semibold py-3 px-8 rounded tracking-wide uppercase transition-colors duration-200"
                >
                  Get Your Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-12 sm:pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#4B0082] p-8 relative transition-colors duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#4B0082]/20 rounded flex items-center justify-center flex-shrink-0">
                    <Image
                      src="https://da1mambadetailing.com/assets/ChatGPT%20Image%20Jul%205,%202025%20at%2011_23_17%20AM_1751750342289.png"
                      alt="DA1MAMBA company logo"
                      width={24}
                      height={24}
                      className="object-contain"
                      unoptimized
                    />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-lg">{service.title}</h2>
                    <p className="text-[#B8860B] text-xs font-medium tracking-wide">{service.subtitle}</p>
                  </div>
                </div>
                <p className="text-[#9CA3AF] text-sm leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-2 mb-8">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#D1D5DB]">
                      <svg className="w-4 h-4 text-[#4B0082] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={`sms:9492935721?body=${encodeURIComponent(service.smsMessage)}`}
                  className="block text-center bg-[#4B0082] hover:bg-[#6B21A8] text-white text-sm font-semibold py-3 rounded tracking-wide uppercase transition-colors duration-200"
                >
                  Get a Quote
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Packages */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">Premium</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Signature Detail Packages</h2>
            <p className="text-[#9CA3AF] max-w-xl mx-auto">For clients who want the full treatment — curated bundles that cover every detail, inside and out.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {packages.map((pkg, i) => (
              <div key={pkg.name} className={`bg-[#1a1a1a] border p-8 ${i === 1 ? 'border-[#4B0082]' : 'border-[#2a2a2a]'}`}>
                <div className="w-8 h-0.5 bg-[#B8860B] mb-6" />
                <h3 className="text-white font-bold text-lg mb-6">{pkg.name}</h3>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#D1D5DB]">
                      <CheckIcon />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a
              href="sms:9492935721"
              className="inline-block bg-[#4B0082] hover:bg-[#6B21A8] text-white text-sm font-semibold py-3 px-10 rounded tracking-wide uppercase transition-colors duration-200"
            >
              Ask About Packages
            </a>
          </div>
        </div>
      </section>

      {/* Add-Ons */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">Add-Ons</p>
            <h2 className="text-3xl font-bold text-white mb-4">Customize Your Detail</h2>
            <p className="text-[#9CA3AF] max-w-xl mx-auto">Pair any service with these targeted treatments for a truly tailored result.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {addOns.map((addon) => (
              <span
                key={addon}
                className="border border-[#2a2a2a] text-[#D1D5DB] text-sm px-5 py-2.5 hover:border-[#4B0082] hover:text-white transition-colors duration-200 cursor-default"
              >
                {addon}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Full Detail Deep-Dive SEO Section */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">Full Detail in Newport Beach, Irvine &amp; Orange County</h2>
          <p className="text-[#9CA3AF] text-sm leading-relaxed mb-10 max-w-4xl">
            Our Full Detail is a complete interior + exterior reset designed for clients who want a vehicle that feels truly new again. Executed with premium products, brand-new microfiber towels for every car (zero cross-contamination), and meticulous technique, Da1Mamba Detail serves discerning owners across Newport Beach, Irvine, and all of Orange County — including luxury, exotic, daily drivers, SUVs, trucks, and boats.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-8">
              <h3 className="text-white font-bold text-lg mb-6">Exterior Included</h3>
              <div className="space-y-3">
                {[
                  'Touchless pre-rinse & premium foam bath',
                  'Wheel, barrel, caliper & tire deep clean',
                  'Contact wash with safe 2-bucket method',
                  'Clay bar & chemical decontamination',
                  'Gentle drying — air + plush microfiber',
                  'Trim clean & dress',
                  'Streak-free glass',
                  'Premium sealant or spray ceramic topper',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-[#D1D5DB]">
                    <CheckIcon />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-8">
              <h3 className="text-white font-bold text-lg mb-6">Interior Included</h3>
              <div className="space-y-3">
                {[
                  'Comprehensive vacuum (seats, carpets, trunk)',
                  'Shampoo & steam treatment',
                  'Leather clean & condition (or fabric deep clean)',
                  'Panels, vents, buttons detailed',
                  'Streak-free interior glass & mirrors',
                  'Professional deodorize & fresh scent',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-[#D1D5DB]">
                    <CheckIcon />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <h3 className="text-white font-bold text-lg mb-6">Popular Enhancements</h3>
          <div className="flex flex-wrap gap-3 mb-10">
            {[
              'Pet hair removal & stain extraction',
              'Ozone/odor neutralization',
              'Water-spot, bug & sap removal',
              'Headlight restoration',
              'Engine bay cleaning',
              'Ceramic coating protection',
              'Paint correction',
            ].map((item) => (
              <span key={item} className="border border-[#2a2a2a] text-[#D1D5DB] text-sm px-4 py-2 hover:border-[#4B0082] hover:text-white transition-colors duration-200">
                {item}
              </span>
            ))}
          </div>
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-6">
            <p className="text-[#B8860B] text-sm font-semibold mb-2">Mobile Full Detail Service Available In:</p>
            <p className="text-[#9CA3AF] text-sm">Newport Beach, Irvine, Laguna Beach, Laguna Niguel, Dana Point, Huntington Beach, Costa Mesa, Mission Viejo, Tustin, Lake Forest, Anaheim, Orange, Santa Ana, and all surrounding Orange County cities.</p>
          </div>
        </div>
      </section>

      {/* Paint Correction Deep-Dive */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">Paint Correction in Newport Beach, Irvine &amp; Orange County</h2>
          <p className="text-[#9CA3AF] text-sm leading-relaxed mb-10 max-w-4xl">
            Our paint correction service restores true clarity and depth by safely removing swirls, light scratches, haze, and oxidation from your clear coat. Executed with precision techniques, premium compounds, and brand-new microfiber towels for every vehicle, this service is trusted by luxury and exotic owners across Newport Beach, Irvine, and greater Orange County.
          </p>
          <h3 className="text-white font-bold text-lg mb-6">Paint Correction Packages</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                level: 'Level 1 — Enhancement (One-Step Polish)',
                desc: 'Refines up to 50–70% of visible defects; boosts gloss and clarity for newer or well-kept finishes.',
              },
              {
                level: 'Level 2 — Correction (Two-Step)',
                desc: 'Compound + polish to remove heavier swirls and light scratches; 70–90% defect removal on most paints.',
              },
              {
                level: 'Level 3 — Signature (Multi-Step)',
                desc: 'Tailored multi-stage correction for severe defects on sensitive or hard clear coats.',
              },
            ].map((pkg) => (
              <div key={pkg.level} className="bg-[#1a1a1a] border border-[#2a2a2a] p-6">
                <div className="w-6 h-0.5 bg-[#B8860B] mb-4" />
                <h4 className="text-white font-semibold mb-3">{pkg.level}</h4>
                <p className="text-[#9CA3AF] text-sm leading-relaxed">{pkg.desc}</p>
              </div>
            ))}
          </div>
          <h3 className="text-white font-bold text-lg mb-6">What Paint Correction Fixes</h3>
          <div className="flex flex-wrap gap-3 mb-10">
            {[
              'Wash marring, holograms & swirl marks',
              'Light to moderate clear-coat scratches',
              'Oxidation, haze & dullness',
              'Water spots & minor etching',
            ].map((item) => (
              <span key={item} className="border border-[#2a2a2a] text-[#D1D5DB] text-sm px-4 py-2 hover:border-[#4B0082] hover:text-white transition-colors duration-200">
                {item}
              </span>
            ))}
          </div>
          <h3 className="text-white font-bold text-lg mb-4">Luxury &amp; Exotic Safe</h3>
          <p className="text-[#9CA3AF] text-sm leading-relaxed mb-8 max-w-4xl">
            Trusted on Ferrari, Lamborghini, McLaren, Porsche, Bentley, Rolls-Royce, Mercedes-AMG, BMW M, Audi RS, and Tesla. Every stage is performed with conservative, paint-safe methodology and fresh materials to eliminate cross-contamination.
          </p>
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-6">
            <p className="text-[#B8860B] text-sm font-semibold mb-2">Mobile Paint Correction Service Available In:</p>
            <p className="text-[#9CA3AF] text-sm">Newport Beach, Irvine, Laguna Beach, Laguna Niguel, Dana Point, Huntington Beach, Costa Mesa, Mission Viejo, Tustin, Lake Forest, Anaheim, Orange, Santa Ana, Garden Grove, and all surrounding Orange County cities.</p>
          </div>
        </div>
      </section>

      {/* Ceramic Coating Deep-Dive */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">Premium Ceramic Coating in Newport Beach, Irvine &amp; Orange County</h2>
          <p className="text-[#9CA3AF] text-sm leading-relaxed mb-10 max-w-4xl">
            Da1Mamba Professional Detailing delivers dealership-grade ceramic coating services to protect the paint on luxury, exotic, and daily-driven vehicles throughout Newport Beach, Irvine, and all of Orange County. Our coatings create a long-term hydrophobic barrier that enhances gloss, reduces maintenance, protects against UV damage, prevents oxidation, and keeps your vehicle cleaner for longer.
          </p>
          <h3 className="text-white font-bold text-lg mb-6">What We Coat</h3>
          <div className="flex flex-wrap gap-3 mb-10">
            {[
              'Paintwork',
              'Wheels, calipers & rims',
              'Glass & windows',
              'Trim & plastics',
              'Interiors (leather, fabric, plastics)',
            ].map((item) => (
              <span key={item} className="border border-[#2a2a2a] text-[#D1D5DB] text-sm px-4 py-2 hover:border-[#4B0082] hover:text-white transition-colors duration-200">
                {item}
              </span>
            ))}
          </div>
          <h3 className="text-white font-bold text-lg mb-4">Trusted By Luxury &amp; Exotic Owners</h3>
          <p className="text-[#9CA3AF] text-sm leading-relaxed mb-8 max-w-4xl">
            We proudly service BMW, Mercedes-Benz, Audi, Porsche, Lamborghini, Ferrari, McLaren, Tesla, Rolls-Royce, Bentley, and high-end SUVs. Every coating is applied using controlled methods, brand-new microfiber towels, and precision paint preparation for maximum adhesion and durability.
          </p>
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-6">
            <p className="text-[#B8860B] text-sm font-semibold mb-2">Mobile Ceramic Coating Service Available In:</p>
            <p className="text-[#9CA3AF] text-sm">Newport Beach, Irvine, Laguna Beach, Laguna Niguel, Costa Mesa, Huntington Beach, Dana Point, Tustin, Mission Viejo, Lake Forest, Anaheim, Orange, Santa Ana, Garden Grove, and all surrounding Orange County cities.</p>
          </div>
        </div>
      </section>

      {/* Interior Detailing Deep-Dive */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">Interior Detailing in Newport Beach, Irvine &amp; Orange County</h2>
          <p className="text-[#9CA3AF] text-sm leading-relaxed mb-10 max-w-4xl">
            Our Interior Detailing service restores a crisp, hygienic cabin with meticulous care and premium techniques. We use brand-new microfiber towels for every vehicle, dedicated interior brushes, and safe chemistry to protect fine materials — ideal for luxury and exotic vehicles as well as daily drivers across Newport Beach, Irvine, and all of Orange County.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2">
              <h3 className="text-white font-bold text-lg mb-6">What&apos;s Included</h3>
              <div className="space-y-3 mb-8">
                {[
                  'Precision vacuum — seats, carpets, mats, trunk, tight crevices',
                  'Targeted stain extraction — carpets, mats, fabric seats',
                  'Shampoo & steam — carpets, fabric, and high-touch areas',
                  'Leather clean & condition — pH-balanced care',
                  'Interior plastics & trim detailed — vents, buttons, switches, consoles',
                  'Streak-free interior glass & mirrors',
                  'Light sanitization & deodorize — neutral, fresh cabin',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-[#D1D5DB]">
                    <CheckIcon />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <h3 className="text-white font-bold text-lg mb-4">Perfect For</h3>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  'Maintenance resets or post-trip refresh',
                  'Kids, pets, beach days, commuting',
                  'Luxury & exotic white-glove care',
                  'Sale prep or post-purchase clean',
                ].map((item) => (
                  <span key={item} className="border border-[#2a2a2a] text-[#D1D5DB] text-sm px-4 py-2 hover:border-[#4B0082] hover:text-white transition-colors duration-200">
                    {item}
                  </span>
                ))}
              </div>
              <h3 className="text-white font-bold text-lg mb-4">Popular Add-Ons</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  'Pet hair removal (heavy)',
                  'Spill & mess cleanup',
                  'Ozone/odor neutralization',
                  'Fabric & leather protection',
                  'Cabin filter check',
                  'Glass water-spot treatment',
                ].map((item) => (
                  <span key={item} className="border border-[#2a2a2a] text-[#D1D5DB] text-sm px-4 py-2 hover:border-[#4B0082] hover:text-white transition-colors duration-200">
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-6 h-fit">
              <p className="text-[#B8860B] text-sm font-semibold mb-2">Mobile Interior Detailing Service Available In:</p>
              <p className="text-[#9CA3AF] text-sm leading-relaxed">Newport Beach, Irvine, Laguna Beach, Laguna Niguel, Dana Point, Huntington Beach, Costa Mesa, Mission Viejo, Tustin, Lake Forest, Anaheim, Orange, Santa Ana, Garden Grove, and all surrounding Orange County cities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Exterior Detailing Deep-Dive */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">Exterior Detailing in Newport Beach, Irvine &amp; Orange County</h2>
          <p className="text-[#9CA3AF] text-sm leading-relaxed mb-10 max-w-4xl">
            Our Exterior Detailing service delivers a crisp, hydrophobic finish with paint-safe methods and premium products. We use brand-new microfiber towels for every vehicle, soft wash media, and controlled techniques to reduce wash-induced marring — trusted by luxury and exotic owners across Newport Beach, Irvine, and all of Orange County.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2">
              <h3 className="text-white font-bold text-lg mb-6">What&apos;s Included</h3>
              <div className="space-y-3 mb-8">
                {[
                  'Touchless pre-rinse & premium foam bath',
                  'Wheel, barrel, caliper & tire deep clean (dedicated tools)',
                  'Hand wash with two-bucket method & grit guards',
                  'Chemical decontamination (iron remover as needed)',
                  'Clay bar decontamination for a glassy-smooth surface',
                  'Gentle drying — filtered air + plush microfiber',
                  'Trim & plastics cleaned and dressed',
                  'Streak-free exterior glass',
                  'Premium sealant or spray ceramic topper for gloss and hydrophobics',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-[#D1D5DB]">
                    <CheckIcon />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <h3 className="text-white font-bold text-lg mb-4">Perfect For</h3>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  'Maintaining a showroom-ready finish',
                  'Pre-sale or post-purchase refresh',
                  'Quarterly decontamination',
                  'Luxury & exotic white-glove wash care',
                ].map((item) => (
                  <span key={item} className="border border-[#2a2a2a] text-[#D1D5DB] text-sm px-4 py-2 hover:border-[#4B0082] hover:text-white transition-colors duration-200">
                    {item}
                  </span>
                ))}
              </div>
              <h3 className="text-white font-bold text-lg mb-4">Recommended Add-Ons</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  'Water spot removal',
                  'Bug, sap & tar removal',
                  'Headlight restoration',
                  'Engine bay cleaning',
                  'Wheel coating or sealant',
                  'One-step polish or Paint Correction',
                  'Ceramic coating (paint, wheels, glass)',
                ].map((item) => (
                  <span key={item} className="border border-[#2a2a2a] text-[#D1D5DB] text-sm px-4 py-2 hover:border-[#4B0082] hover:text-white transition-colors duration-200">
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-6 h-fit">
              <p className="text-[#B8860B] text-sm font-semibold mb-2">Mobile Exterior Detailing Service Available In:</p>
              <p className="text-[#9CA3AF] text-sm leading-relaxed">Newport Beach, Irvine, Laguna Beach, Laguna Niguel, Dana Point, Huntington Beach, Costa Mesa, Mission Viejo, Tustin, Lake Forest, Anaheim, Orange, Santa Ana, Garden Grove, and all surrounding Orange County cities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1a0030] via-[#2d0050] to-[#0a0a0a] text-center">
        <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-4">Get Started</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Your Car Deserves Better Than a Drive-Through Wash.</h2>
        <p className="text-[#D1D5DB] mb-10">We come to you. Fast response, fair pricing, and results that speak for themselves.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
          <a
            href="sms:9492935721"
            className="w-full sm:w-auto bg-[#4B0082] hover:bg-[#6B21A8] text-white font-semibold px-10 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200"
          >
            Text for a Quote
          </a>
          <a
            href="tel:+19492935721"
            className="w-full sm:w-auto border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-black font-semibold px-10 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200"
          >
            Call (949) 293-5721
          </a>
        </div>
        <p className="text-[#9CA3AF] text-xs">Serving Newport Beach, Irvine, and all of Orange County.</p>
      </section>

      <Footer />
    </div>
  );
}
